<template>
  <div
    class="multi-store-options multi-store-options--modal form-group"
    :class="`multi-store-options--${namespace}`"
  >
    <div class="multi-store-options__country-container form-group__field">
      <select
        :id="`${namespace}-country-selector`"
        v-model="currentCountryCode"
        class="multi-store-options__country"
        name="country_code"
      >
        <option
          v-for="(country) of countries"
          :key="country.code"
          :value="country.code"
          v-text="getCountryOption(country)"
        />
      </select>
      <label
        class="form-group__label"
        :for="`${namespace}-country-selector`"
        v-text="$string('general.multi_store.options.country')"
      />
    </div>

    <div
      v-if="languageSelector && languages?.length > 1"
      class="multi-store-options__language-container form-group__field"
    >
      <select
        :id="`${namespace}-language-selector`"
        v-model="currentLanguageCode"
        class="multi-store-options__language"
        name="language_code"
      >
        <option
          v-for="(language) of languages"
          :key="language?.code"
          :value="language?.code"
          v-text="getLanguageOption(language)"
        />
      </select>
      <label
        class="form-group__label"
        :for="`${namespace}-language-selector`"
        v-text="$string('general.multi_store.options.language')"
      />
    </div>

    <input
      v-else
      name="language_code"
      type="hidden"
      :value="currentLanguageCode"
    >
  </div>
</template>

<script>

/**
 * Vue: Multi-store options (multi-store-options)
 * -----------------------------------------------------------------------------
 * Localization form selects with options.
 * - For use in modal.
 * - Select names have to those names as they're set by Shopify.
 *
 * @param {Boolean} [languageSelector] - Display language selector.
 * @param {String} namespace - Namespace of component.
 *
 */
import { mapActions, mapState } from 'vuex'

import { replaceMultiStoreStrings } from '~/helpers/multi-store'

export default {
  name: 'MultiStoreOptions',

  props: {
    languageSelector: {
      type: Boolean,
      default: true,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
  },

  data() {
    return {
      currentCountryCode: '',
      currentLanguageCode: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      current: (state) => state['multi-store'].current,
      selectedStore: (state) => state['multi-store'].selectedStore,
      suggestedStore: (state) => state['multi-store'].suggestedStore,
      supported: (state) => state['multi-store'].supported,
    }),

    /**
     * Compute active value.
     * - This is selected if user has changed select value, or suggested if user
     *   hasn't updated anything yet.
     */
    active() {
      return this.selectedStore.country ? this.selectedStore : this.suggestedStore
    },

    /**
     * Compute countries array.
     * @returns {Array}
     */
    countries() {
      if (!Object.keys(this.supported).length) {
        return [this.current.country]
      }

      return Object.values(this.supported)
        .map((value) => value.country)
        .sort((a, b) => {
          return a.name.localeCompare(
            b.name,
            this.current.language.code.toLowerCase(),
          )
        })
    },

    /**
     * Compute language choices based on current country.
     * @returns {Array}
     */
    languages() {
      return this.supported[this.currentCountryCode]?.languages?.length
        ? this.supported[this.currentCountryCode].languages
        : [this.current.language]
    },
  },

  watch: {

    /**
     * Watch current country code to update select width.
     * - Also tests to see if previously selected language exists in country.
     * @param {String} countryCode - New country code.
     */
    currentCountryCode(countryCode) {
      if (!countryCode) {
        return
      }

      const matchedLanguage = this.languages.find((language) => {
        return language.code?.toUpperCase() === this.currentLanguageCode?.toUpperCase()
      })

      if (!matchedLanguage) {
        this.currentLanguageCode = this.languages[0].code?.toUpperCase()
      }

      if (this.supported[countryCode]) {
        this.setSelectedStore({
          country: this.supported[countryCode].country,
          instance: this.supported[countryCode].instance,
          language: matchedLanguage || this.languages[0],
        })
      }
    },

    /**
     * Watch current language code to update select width.
     * @param {String} languageCode - New language code.
     */
    currentLanguageCode(languageCode) {
      const matchedLanguage = this.languages.find((language) => {
        return language.code?.toUpperCase() === languageCode?.toUpperCase()
      })

      if (this.supported[this.currentCountryCode]) {
        this.setSelectedStore({
          country: this.supported[this.currentCountryCode].country,
          instance: this.supported[this.currentCountryCode].instance,
          language: matchedLanguage || this.languages[0],
        })
      }
    },

    /**
     * Watch suggested store to update default selection.
     */
    suggestedStore() {
      this.setCurrentValues()
    },
  },

  mounted() {
    this.setCurrentValues()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      setPreference: 'multi-store/setPreference',
      setSelectedStore: 'multi-store/setSelectedStore',
    }),

    /**
     * Set current values.
     */
    setCurrentValues() {
      if (this.currentCountryCode || this.currentLanguageCode) {
        return
      }

      this.currentCountryCode = this.suggestedStore.country?.code
      this.currentLanguageCode = this.suggestedStore.language?.code?.toUpperCase()
    },

    /**
     * Get country option.
     * @param {Object} country - Current country.
     * @returns {String}
     */
    getCountryOption(country) {
      const string = 'general.multi_store.format.country'

      return this.$string(
        string,
        { replace: this.replaceStrings({ country }) },
      )
    },

    /**
     * Get language option.
     * @param {Object} language - Current language.
     * @returns {String}
     */
    getLanguageOption(language) {
      return this.$string(
        'general.multi_store.format.language',
        { replace: this.replaceStrings({ language }) },
      )
    },

    /**
     * Replace text shortcodes.
     * @returns {Object}
     */
    replaceStrings({ country, language } = {}) {
      return replaceMultiStoreStrings(country, language)
    },
  },
}
</script>

<style lang="scss">
@import './multi-store-options';
</style>
